import type {
	GenericThreadHistoryAdapter,
	MessageFormatAdapter,
	MessageFormatItem,
	MessageFormatRepository,
	ThreadHistoryAdapter,
} from '@assistant-ui/react';
import type { UIMessage } from 'ai';

/** Returns the id of the conversation bound to the runtime, or undefined for an uninitialised new thread. */
export type ActiveConversationIdReader = () => string | undefined;

/** Loads and normalises the persisted message history for a conversation. */
export type ConversationMessageLoader = (conversationId: string) => Promise<UIMessage[]>;

/**
 * Message-history adapter for the agent-workbench runtime.
 *
 * History is loaded through this adapter inside an effect and the thread is
 * hydrated with the result — the runtime never suspends on history loading.
 * That guarantee is load-bearing: assistant-ui's thread-switch machinery awaits
 * the hosted runtime hook rendering to completion before promoting the selected
 * conversation to the active thread, so a hook that suspends stalls the switch
 * and leaves the pane on the empty thread.
 */
export class AgentWorkbenchMessageHistoryAdapter implements ThreadHistoryAdapter {
	public constructor(
		private readonly getActiveConversationId: ActiveConversationIdReader,
		private readonly loadMessages: ConversationMessageLoader,
	) {}

	/**
	 * Part of {@link ThreadHistoryAdapter} but unused with `useAISDKRuntime`,
	 * which reads history exclusively through the `withFormat` adapter.
	 *
	 * @returns An empty repository.
	 */
	public load() {
		return Promise.resolve({ messages: [] });
	}

	/** No-op: history is persisted server-side by the streaming backend, never written back from the client. */
	public append(): Promise<void> {
		return Promise.resolve();
	}

	/**
	 * Format-aware adapter through which `useAISDKRuntime` loads history.
	 *
	 * @returns An adapter whose `load` yields the conversation history as a
	 *   single linear branch.
	 */
	public withFormat<TMessage, TStorageFormat extends Record<string, unknown>>(
		_formatAdapter: MessageFormatAdapter<TMessage, TStorageFormat>,
	): GenericThreadHistoryAdapter<TMessage> {
		const adapter: GenericThreadHistoryAdapter<UIMessage> = {
			load: () => this.loadRepository(),
			append: () => Promise.resolve(),
		};
		// The runtime only ever instantiates this with the AI-SDK format, whose
		// message type is `UIMessage`; the persisted history is already in that
		// shape, so the format adapter is unused and the generic collapses to it.
		return adapter as unknown as GenericThreadHistoryAdapter<TMessage>;
	}

	/**
	 * Loads the active conversation's history as a repository. A persisted
	 * conversation is a single linear branch of assistant-ui's message tree, so
	 * each message is chained to its predecessor and the head points at the last.
	 *
	 * @returns The message repository, empty when no conversation is bound yet.
	 */
	private async loadRepository(): Promise<MessageFormatRepository<UIMessage>> {
		const conversationId = this.getActiveConversationId();
		if (!conversationId) {
			return { messages: [] };
		}
		const messages = await this.loadMessages(conversationId);
		const items: MessageFormatItem<UIMessage>[] = [];
		let parentId: string | null = null;
		for (const message of messages) {
			items.push({ parentId, message });
			parentId = message.id;
		}
		return { headId: parentId, messages: items };
	}
}
